import { Drawer, Stack, useTheme } from "@mui/material";
import { FoodsIconButton, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import type { AuditLogEntry } from "src/api/client-api/auditLog/auditLogSchemas";
import { useGetAuditLogEntry } from "src/api/client-api/auditLog/useGetAuditLogEntry";
import { AuditLogDetail } from "./AuditLogDetail";
import type { AuditLogQueryFilters } from "./auditLogQuery";

export const AUDIT_LOG_DRAWER_WIDTH = 50;
const AUDIT_LOG_DRAWER_TITLE_ID = "audit-log-drawer-title";

type AuditLogDrawerProps = {
    entry: AuditLogEntry | null;
    onClose: () => void;
    isMobile: boolean;
    onFocusFilters: (filters: AuditLogQueryFilters) => void;
    onViewObjectHistory: (filters: AuditLogQueryFilters) => void;
};

export const AuditLogDrawer = (props: AuditLogDrawerProps) => {
    const { entry, onClose, isMobile, onFocusFilters, onViewObjectHistory } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const { entry: detailedEntry } = useGetAuditLogEntry(entry?.id);

    const displayedEntry = detailedEntry ?? entry;

    return (
        <Drawer
            anchor="right"
            open={entry !== null}
            onClose={onClose}
            PaperProps={{
                role: "dialog",
                "aria-modal": true,
                "aria-labelledby": AUDIT_LOG_DRAWER_TITLE_ID,
                sx: {
                    width: isMobile ? "100%" : theme.spacing(AUDIT_LOG_DRAWER_WIDTH),
                    maxWidth: "100%",
                },
            }}
        >
            {displayedEntry !== null && displayedEntry !== undefined && (
                <Stack padding={theme.spacing(3)} gap={theme.spacing(2)}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={theme.spacing(1)}>
                        <Stack direction="row" alignItems="center" gap={theme.spacing(1)}>
                            {isMobile && (
                                <FoodsIconButton
                                    icon="arrowLeftShort"
                                    size="s"
                                    tooltip={{ text: t("settings.audit_log.detail.back", "Back to the log") }}
                                    buttonProps={{ onClick: onClose }}
                                />
                            )}
                            <Typography id={AUDIT_LOG_DRAWER_TITLE_ID} variant="h4">
                                {t("settings.audit_log.detail.title", "Entry detail")}
                            </Typography>
                        </Stack>
                        {!isMobile && (
                            <FoodsIconButton
                                icon="close"
                                size="s"
                                tooltip={{ text: t("common.close", "Close") }}
                                buttonProps={{ onClick: onClose }}
                            />
                        )}
                    </Stack>

                    <AuditLogDetail
                        entry={displayedEntry}
                        isStacked={isMobile}
                        onFocusFilters={onFocusFilters}
                        onViewObjectHistory={onViewObjectHistory}
                    />
                </Stack>
            )}
        </Drawer>
    );
};
